/**
 *
 * CardLayout
 *
 */
import {
  Card,
  CardActionArea,
  CardActions,
  CardContent,
  CardHeader,
  Box,
  Collapse,
} from '@mui/material';
import React, { ReactNode } from 'react';
import clsx from 'clsx';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { makeThemeStyle } from 'styles/makeThemeStyle';

interface StyleProps {
  overrideCardStyle?: string;
  overrideCardHeaderStyle?: string;
  overrideCardActionAreaStyle?: string;
  overrideCardActionStyle?: string;
  overrideCardContentStyle?: string;
  overrideExpandBtn?: string;
}

export interface Props extends StyleProps {
  action?: ReactNode;
  title?: ReactNode;
  subheader?: ReactNode;
  cardContent?: ReactNode;
  cardAction?: ReactNode;
  contentArea?: boolean;
  actionArea?: boolean; //Whether wrap CardActionArea to CardContent
  requireExpand?: boolean; //Whether want to have expand action (Icon + Collapse) in CardAction
  collapsedContent?: ReactNode; //Elements that is used when requireExpand is enabled
  expandOnFirstLoad?: boolean; //Whether want to expand on first load
  disableCardContentClick?: boolean; //Whether disable the clickable feature for card content (Require actionArea to be true)
}

const useStyles = makeThemeStyle(theme => ({
  card: {
    borderRadius: 5,
    boxShadow: 'unset',
    borderColor: theme.palette.border,
  },
  cardHeader: {
    borderBottom: `1px solid ${theme.palette.border}`,
  },
  cardAction: {
    borderTop: `1px solid ${theme.palette.border}`,
    justifyContent: 'space-between',
  },
  cardActionContent: {
    '& > *': {
      marginLeft: theme.spacing(1),
    },
  },
  cardHeaderTitle: {
    display: 'grid',
  },
  expandBtn: {
    float: 'right',
    transform: 'rotate(0deg)',
    transition: theme.transitions.create('transform', {
      duration: theme.transitions.duration.shortest,
    }),
  },
  expanded: {
    transform: 'rotate(180deg)',
  },
}));

export const CardLayout: React.FC<Props> = ({
  action,
  title,
  subheader,
  cardContent,
  cardAction,
  contentArea = true,
  actionArea = true,
  requireExpand = false,
  collapsedContent = null,
  expandOnFirstLoad = false,
  disableCardContentClick = false,
  ...rest
}) => {
  const classes = useStyles(rest);
  const [expanded, setExpanded] = React.useState(expandOnFirstLoad);

  const handleExpandClick = () => {
    setExpanded(!expanded);
  };
  let muiCardContent = (
    <CardContent className={clsx([rest.overrideCardContentStyle])}>
      {cardContent}
    </CardContent>
  );

  if (actionArea) {
    muiCardContent = (
      <CardActionArea
        disabled={disableCardContentClick}
        className={clsx([rest.overrideCardActionAreaStyle])}
      >
        {muiCardContent}
      </CardActionArea>
    );
  }

  const RenderExpandMoreIcon = requireExpand ? (
    <Box
      className={clsx({ [classes.expanded]: expanded }, [
        classes.expandBtn,
        rest.overrideExpandBtn,
      ])}
      aria-label="CardLayout expand more"
    >
      <ExpandMoreIcon />
    </Box>
  ) : null;

  let muiCardAction =
    cardAction || requireExpand ? (
      <CardActions
        className={clsx([classes.cardAction, rest.overrideCardActionStyle])}
      >
        <Box
          display="flex"
          flexGrow={2}
          alignItems="baseline"
          className={classes.cardActionContent}
        >
          {cardAction}
        </Box>
        {RenderExpandMoreIcon}
      </CardActions>
    ) : null;

  if (requireExpand) {
    muiCardAction = (
      <CardActionArea onClick={handleExpandClick}>
        {muiCardAction}
      </CardActionArea>
    );
  }

  return (
    <>
      <Card
        className={clsx([classes.card, rest.overrideCardStyle])}
        variant="outlined"
      >
        <CardHeader
          action={action}
          title={title}
          subheader={subheader}
          className={clsx([classes.cardHeader, rest.overrideCardHeaderStyle])}
          classes={{
            title: classes.cardHeaderTitle,
          }}
        />
        {contentArea && muiCardContent}
        {muiCardAction}
        {requireExpand && (
          <Collapse in={expanded} timeout="auto">
            <CardContent>{collapsedContent}</CardContent>
          </Collapse>
        )}
      </Card>
    </>
  );
};

export default CardLayout;
